import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Rating,
  Paper,
  Divider,
  Grid as MuiGrid,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import type { PropertyRecommendation as PropertyRecommendationType } from '../../services/propertyService';
import propertyService from '../../services/propertyService';

// Use Grid as a custom component to fix MUI version issues
const Grid = MuiGrid;

const PropertyRecommendation: React.FC = () => {
  const [recommendations, setRecommendations] = useState<PropertyRecommendationType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Filter states
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [guestCount, setGuestCount] = useState<number>(1);
  const [preferredLocation, setPreferredLocation] = useState<string>('');
  
  const fetchRecommendations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const filters = {
        start_date: startDate?.toISOString().split('T')[0],
        end_date: endDate?.toISOString().split('T')[0],
        guest_count: guestCount,
        preferred_location: preferredLocation,
      };
      
      const data = await propertyService.getPropertyRecommendations(filters);
      setRecommendations(data);
    } catch (err) {
      setError('Failed to fetch property recommendations. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, guestCount, preferredLocation]);
  
  useEffect(() => {
    fetchRecommendations();
  }, [fetchRecommendations]);
  
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchRecommendations();
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Property Recommendations
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <form onSubmit={handleSearch}>
          <Grid container spacing={2}>
            <Grid style={{ gridColumn: 'span 3' }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid style={{ gridColumn: 'span 3' }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid style={{ gridColumn: 'span 2' }}>
              <TextField
                label="Number of Guests"
                type="number"
                value={guestCount}
                onChange={(e) => setGuestCount(Number(e.target.value))}
                fullWidth
                inputProps={{ min: 1 }}
              />
            </Grid>
            
            <Grid style={{ gridColumn: 'span 3' }}>
              <TextField
                label="Preferred Location"
                value={preferredLocation}
                onChange={(e) => setPreferredLocation(e.target.value)}
                fullWidth
              />
            </Grid>
            
            <Grid style={{ gridColumn: 'span 1' }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                sx={{ height: '56px' }}
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {recommendations.map((property) => (
            <Grid style={{ gridColumn: 'span 4' }} key={property.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {property.name}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Rating
                      value={property.recommendation_score / 20}
                      precision={0.5}
                      readOnly
                      size="small"
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      {property.recommendation_score.toFixed(1)}%
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Grid container spacing={2}>
                    <Grid style={{ gridColumn: 'span 6' }}>
                      <Typography variant="body2" color="text.secondary">
                        Occupancy Rate
                      </Typography>
                      <Typography variant="body1">
                        {property.occupancy_rate.toFixed(1)}%
                      </Typography>
                    </Grid>
                    
                    <Grid style={{ gridColumn: 'span 6' }}>
                      <Typography variant="body2" color="text.secondary">
                        Availability
                      </Typography>
                      <Typography variant="body1">
                        {property.availability.available_rooms} / {property.availability.total_rooms} rooms
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default PropertyRecommendation; 